import { type FC, type ReactNode } from 'react';

import { Button } from '@vakantiesnl/components/src/atoms/Button';
import { Icon, type IconTypes } from '@vakantiesnl/components/src/atoms/Icon';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';

import { useCustomStyles } from './Alert.style';

export type AlertVariant = 'success' | 'warning' | 'info' | 'error';

type optionalAlertProps =
	| { title: string; text: ReactNode }
	| { title?: string; text: ReactNode }
	| { title: string; text?: ReactNode };

type AlertProps = optionalAlertProps & {
	id?: string;
	variant?: AlertVariant;
	className?: string;
	withIcon?: boolean;
	/*
	 * A microcopy string containing the asset ID for an image.
	 * Pass the microcopy with the specific asset ID for the image you want to display.
	 */
	imageMicrocopy?: string;
	alt?: string;
	/** Custom icon to overrule the variant icon */
	customIcon?: IconTypes;
	hyperlink?: {
		title: string;
		link: string;
	} | null;
	dataCy?: string;
	onClose?: VoidFunction;
};

const getIconType: Record<AlertVariant, 'checkmark' | 'info' | 'warning' | 'cancel'> = {
	success: 'checkmark',
	warning: 'warning',
	info: 'info',
	error: 'cancel',
};

export const Alert: FC<AlertProps> = ({
	title,
	text,
	variant = 'success',
	className,
	withIcon = true,
	id,
	customIcon,
	hyperlink,
	dataCy = 'alert-container',
	onClose,
}) => {
	const { classes, cx } = useCustomStyles(variant);

	const { isTabletOrIsDesktop } = useBreakpoints();

	return (
		<div className={cx(classes.container, classes[variant], className)} data-cy={dataCy} id={id}>
			{withIcon && <Icon size="medium" type={customIcon ?? getIconType[variant]} className={classes.icon} />}
			<div className={classes.alertTitleTextWrapper}>
				{title && (
					<Typography variant="headingXs" as="h6" className={classes.title} data-cy={`${dataCy}-title`}>
						{title}
					</Typography>
				)}
				{text && (
					<Typography
						variant={isTabletOrIsDesktop ? 'bodyMd' : 'bodySm'}
						as="div"
						className={classes.text}
						data-cy={`${dataCy}-text`}
					>
						{text}
					</Typography>
				)}
				{hyperlink && (
					<Button className={classes.button} size="small" asText href={hyperlink.link}>
						{hyperlink.title}
					</Button>
				)}
			</div>
			{onClose && <Button leadingIcon="cancel" onClick={onClose} size="small" variant="tertiary" />}
		</div>
	);
};
